<template>
  <b-modal
    id="namecheck_modal"
    ref="namecheckModal"
    title="Check name"
    centered
    scrollable
    no-stacking
    modal-class="nc-modal"
    dialog-class="nc-modal-dialog"
    header-class="align-items-center"
    @show="onShow"
    @hidden="onHidden"
  >
    <!-- The header is left to BootstrapVue: it wires aria-labelledby to its own generated
         title id, which a custom #modal-header slot would silently break. -->
    <div class="nc-form">
      <!-- Payout type. Drives which list the institution select shows and how the
           number is normalised, so it comes first. -->
      <div class="nc-toggle" role="group" aria-label="Account type">
        <button
          type="button"
          class="nc-toggle__btn"
          :class="{ 'is-active': payoutType === 'Mobile Money' }"
          :aria-pressed="payoutType === 'Mobile Money' ? 'true' : 'false'"
          @click="setPayoutType('Mobile Money')"
        >
          Mobile Money
        </button>
        <button
          type="button"
          class="nc-toggle__btn"
          :class="{ 'is-active': payoutType === 'Bank Deposit' }"
          :aria-pressed="payoutType === 'Bank Deposit' ? 'true' : 'false'"
          @click="setPayoutType('Bank Deposit')"
        >
          Bank Account
        </button>
      </div>

      <div class="form-group">
        <label for="nc-country">Country <span class="nc-required">*</span></label>
        <select
          id="nc-country"
          v-model="isocode"
          class="form-control"
          required
          @change="onCountryChange"
        >
          <option v-for="country in destinationCountries" :key="country.isocode" :value="country.isocode">
            {{ country.countryname }}
          </option>
        </select>
      </div>

      <div class="form-group">
        <label for="nc-institution">
          {{ isMomo ? "Mobile network" : "Bank" }} <span class="nc-required">*</span>
        </label>
        <select
          id="nc-institution"
          v-model="institutionName"
          class="form-control"
          required
          :disabled="institutionsLoading || institutions.length === 0"
          :aria-describedby="institutionsMessage ? 'nc-institution-help' : null"
        >
          <option value="" disabled>
            {{ institutionPlaceholder }}
          </option>
          <option v-for="item in institutions" :key="item.payangelCode" :value="item.name">
            {{ item.name }}
          </option>
        </select>
        <small v-if="institutionsMessage" id="nc-institution-help" class="nc-hint">
          {{ institutionsMessage }}
        </small>
      </div>

      <div class="form-group">
        <label for="nc-account">
          {{ isMomo ? "Mobile number" : "Account number" }} <span class="nc-required">*</span>
        </label>
        <input
          id="nc-account"
          v-model="accountNumberRaw"
          class="form-control"
          :class="{ 'is-invalid': showAccountError }"
          type="text"
          inputmode="numeric"
          autocomplete="off"
          required
          :placeholder="isMomo ? 'e.g. 0505922888' : 'e.g. 0123456789'"
          :aria-invalid="showAccountError ? 'true' : 'false'"
          aria-describedby="nc-account-help"
          @blur="accountTouched = true"
          @keyup.enter="runCheck"
        />
        <span v-if="showAccountError" class="invalid-feedback">
          Enter the {{ isMomo ? "mobile number" : "account number" }} to check.
        </span>
        <!-- Shows exactly what gets sent, so a mangled paste is visible before the check
             runs rather than being blamed on the account. -->
        <small id="nc-account-help" class="nc-hint">
          <template v-if="normalisedAccountNumber">
            Will check: <strong>{{ normalisedAccountNumber }}</strong>
          </template>
          <template v-else-if="isMomo">
            Paste any format — the country code is added automatically.
          </template>
          <template v-else> Digits only. </template>
        </small>
      </div>

      <!-- Result region. aria-live so the outcome is announced without moving focus
           away from the form the operator is still working in. -->
      <div aria-live="polite">
        <div v-if="checking" class="nc-result" aria-busy="true">
          <span class="sr-only">Checking name…</span>
          <div class="nc-skeleton__bar nc-skeleton__bar--title" aria-hidden="true"></div>
          <div class="nc-skeleton__bar nc-skeleton__bar--name" aria-hidden="true"></div>
          <div class="nc-skeleton__bar nc-skeleton__bar--meta" aria-hidden="true"></div>
        </div>

        <div v-else-if="result" class="nc-result">
          <div class="nc-result__head">
            <h6 class="nc-result__title">{{ resultView.title }}</h6>
            <span class="nc-status" :class="resultView.pillClass">{{ resultView.pill }}</span>
          </div>

          <div class="nc-result__body">
            <div>
              <p v-if="result.status === STATUS.FOUND" class="nc-result__name">{{ result.name }}</p>
              <p v-else class="nc-result__message">{{ resultView.message }}</p>
              <p class="nc-result__meta">{{ checkedSummary }}</p>
            </div>
            <div class="nc-result__actions">
              <button
                v-if="result.status === STATUS.FOUND"
                type="button"
                class="nc-btn nc-btn--outline nc-btn--sm"
                @click="copyName"
              >
                Copy
              </button>
              <button
                v-if="result.status === STATUS.PROVIDER_ERROR"
                type="button"
                class="nc-btn nc-btn--outline nc-btn--sm"
                @click="runCheck"
              >
                Retry
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <template #modal-footer>
      <button type="button" class="nc-btn nc-btn--outline" @click="hide">Close</button>
      <button type="button" class="nc-btn nc-btn--primary" :disabled="!canCheck" @click="runCheck">
        <i v-if="checking" class="fa fa-spinner fa-spin mr-1" aria-hidden="true"></i>
        {{ checking ? "Checking…" : "Check name" }}
      </button>
    </template>
  </b-modal>
</template>

<script>
import { mapGetters } from "vuex";
import {
  COUNTRYLIST,
  GET_NAMECHECK_INSTITUTIONS,
  RUN_NAMECHECK,
} from "@/store/actions.type";
import { normaliseAccountNumber } from "@/utils/namecheck/normaliseAccountNumber";
import { NAME_CHECK_STATUS } from "@/utils/namecheck/status";

const DEFAULT_ISOCODE = "GH";

export default {
  name: "NameCheckModal",
  data() {
    return {
      // Country and payout type persist across open/close for the session — an operator
      // works one corridor at a time. The number and result do not (see onHidden).
      payoutType: "Mobile Money",
      isocode: DEFAULT_ISOCODE,
      institutionName: "",
      accountNumberRaw: "",
      institutionsLoading: false,
      institutionsFailed: false,
      checking: false,
      result: null,
      accountTouched: false,
    };
  },
  computed: {
    ...mapGetters(["countryList"]),

    STATUS() {
      return NAME_CHECK_STATUS;
    },

    isMomo() {
      return this.payoutType === "Mobile Money";
    },

    /**
     * Payout destinations. Falls back to the full list if nothing carries the destination
     * flag, so a data gap never leaves the operator with an empty country dropdown.
     */
    destinationCountries() {
      const all = this.countryList || [];
      const destinations = all.filter((c) => Number(c.destination) === 1);
      return destinations.length > 0 ? destinations : all;
    },

    selectedCountry() {
      return (this.countryList || []).find((c) => c.isocode === this.isocode) || null;
    },

    dialCode() {
      return this.selectedCountry ? this.selectedCountry.mobilecountrycode || "" : "";
    },

    institutionsForCountry() {
      return this.$store.getters.namecheckInstitutions(this.isocode);
    },

    institutions() {
      const lists = this.institutionsForCountry;
      if (!lists) return [];
      return (this.isMomo ? lists.mobile : lists.banks) || [];
    },

    institutionPlaceholder() {
      if (this.institutionsLoading) return "Loading…";
      if (this.institutions.length === 0) return "None available";
      return this.isMomo ? "Select network" : "Select bank";
    },

    institutionsMessage() {
      if (this.institutionsLoading) return "";
      if (this.institutionsFailed) {
        return "The bank and network list could not be loaded. Close and reopen to try again.";
      }
      if (this.institutionsForCountry && this.institutions.length === 0) {
        const what = this.isMomo ? "mobile networks" : "banks";
        return `No ${what} can be name-checked for ${this.countryLabel}.`;
      }
      return "";
    },

    countryLabel() {
      return this.selectedCountry ? this.selectedCountry.countryname : this.isocode;
    },

    normalisedAccountNumber() {
      return normaliseAccountNumber(this.accountNumberRaw, this.payoutType, this.dialCode);
    },

    showAccountError() {
      return this.accountTouched && !this.normalisedAccountNumber;
    },

    canCheck() {
      return (
        !this.checking &&
        !!this.isocode &&
        !!this.institutionName &&
        !!this.normalisedAccountNumber
      );
    },

    /** What was actually sent — echoed back so a wrong selection is obvious. */
    checkedSummary() {
      const parts = [this.institutionName, this.normalisedAccountNumber, this.countryLabel];
      return parts.filter(Boolean).join(" · ");
    },

    resultView() {
      switch (this.result && this.result.status) {
        case NAME_CHECK_STATUS.FOUND:
          return {
            title: "Name found",
            pill: "Verified",
            pillClass: "nc-status--success",
            message: "",
          };
        case NAME_CHECK_STATUS.NOT_FOUND:
          return {
            title: "No name returned",
            pill: "No match",
            pillClass: "nc-status--default",
            message: `No account holder name was returned for this ${
              this.isMomo ? "mobile number" : "account number"
            }. Check the digits and the ${this.isMomo ? "network" : "bank"}, then try again.`,
          };
        case NAME_CHECK_STATUS.UNSUPPORTED:
          return {
            title: "Not supported",
            pill: "Not supported",
            pillClass: "nc-status--warning",
            message: `Name check is not available for ${this.institutionName} in ${this.countryLabel}. The account may still be valid — this check simply cannot verify it.`,
          };
        default:
          return {
            title: "Check unavailable",
            pill: "Unavailable",
            pillClass: "nc-status--danger",
            message:
              "The name check service did not respond. This says nothing about the account — try again in a moment.",
          };
      }
    },
  },
  watch: {
    accountNumberRaw() {
      // A new number invalidates the displayed result: never leave a name sitting above
      // an account it does not belong to.
      this.result = null;
    },
    institutionName() {
      this.result = null;
    },
  },
  methods: {
    async onShow() {
      this.accountTouched = false;
      if (!this.countryList || this.countryList.length === 0) {
        await this.$store.dispatch(COUNTRYLIST);
      }
      if (!this.destinationCountries.some((c) => c.isocode === this.isocode)) {
        const fallback = this.destinationCountries[0];
        this.isocode = fallback ? fallback.isocode : DEFAULT_ISOCODE;
      }
      await this.loadInstitutions();
      // A network or bank kept from a previous session may no longer be in the list
      // (different country, or the partner dropped it). Clear it rather than letting the
      // form look valid while the select shows nothing.
      if (this.institutionName && !this.institutions.some((i) => i.name === this.institutionName)) {
        this.institutionName = "";
      }
    },

    onHidden() {
      this.accountNumberRaw = "";
      this.result = null;
      this.accountTouched = false;
      this.checking = false;
    },

    hide() {
      this.$bvModal.hide("namecheck_modal");
    },

    setPayoutType(type) {
      if (this.payoutType === type) return;
      this.payoutType = type;
      this.institutionName = "";
      this.result = null;
    },

    async onCountryChange() {
      this.institutionName = "";
      this.result = null;
      await this.loadInstitutions();
    },

    async loadInstitutions() {
      this.institutionsLoading = true;
      this.institutionsFailed = false;
      const details = await this.$store.dispatch(GET_NAMECHECK_INSTITUTIONS, this.isocode);
      this.institutionsFailed = !details;
      this.institutionsLoading = false;
    },

    async runCheck() {
      this.accountTouched = true;
      if (!this.canCheck) return;

      this.checking = true;
      this.result = null;
      try {
        const details = await this.$store.dispatch(RUN_NAMECHECK, {
          isocode: this.isocode,
          payoutType: this.payoutType,
          accountNumber: this.normalisedAccountNumber,
          networkOrBankName: this.institutionName,
        });
        this.result = details || { status: NAME_CHECK_STATUS.PROVIDER_ERROR };
      } finally {
        this.checking = false;
      }
    },

    async copyName() {
      const name = this.result && this.result.name;
      if (!name) return;
      try {
        await navigator.clipboard.writeText(name);
        this.$toast.success("Name copied", { position: "top-right" });
      } catch (error) {
        this.$toast.error("Could not copy the name", { position: "top-right" });
      }
    },
  },
};
</script>

<!-- Not scoped: BootstrapVue renders the dialog's inner elements outside this component's
     template scope, so scoped attributes would never reach .nc-modal-dialog. Every selector
     is nc-* prefixed instead, the same approach as reroute-drawer.css. -->
<style src="@/assets/css/namecheck-modal.css"></style>
